import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  AutocompleteFilter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { createPublisherAppDraft } from '../../publisherAppsImport';
import type { PublisherAppImportDraft } from '../../model';

export function PublisherAppsEditorTable({
  rows,
  onRowsChange,
  platforms,
  iabCategories,
  disabled,
  preview,
}: {
  rows: PublisherAppImportDraft[];
  onRowsChange: (rows: PublisherAppImportDraft[]) => void;
  platforms: Array<{ id: string; name: string }>;
  iabCategories: Array<{ id: string; name: string }>;
  disabled: boolean;
  preview: boolean;
}) {
  const [rowsToAdd, setRowsToAdd] = React.useState('1');
  const gridTemplateColumns = preview
    ? '1.1fr 1.2fr 1fr 1fr 1fr 1fr 1.4fr 44px'
    : '1.1fr 1fr 1fr 1fr 1fr 1.4fr 44px';

  function update(rowId: string, patch: Partial<PublisherAppImportDraft>) {
    onRowsChange(rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  }

  return (
    <div className="space-y-3">
      {!preview ? <p className="text-text-secondary text-sm">Apps in list: {rows.length}</p> : null}
      <div className="border-border overflow-x-auto rounded-lg border">
        <div className={preview ? 'min-w-[1250px]' : 'min-w-[1050px]'}>
          <div
            className="bg-muted grid gap-3 px-3 py-3 text-xs font-semibold uppercase"
            style={{ gridTemplateColumns }}
          >
            <span>Name</span>
            {preview ? <span>App store URL</span> : null}
            <span>App store ID *</span>
            <span>Platform *</span>
            <span>Bundle (vanity) ID</span>
            <span>App domain</span>
            <span>IAB categories</span>
            <span />
          </div>
          {rows.map((row) => (
            <div
              key={row.rowId}
              className="border-border grid items-start gap-3 border-t px-3 py-3"
              style={{ gridTemplateColumns }}
            >
              <Input
                aria-label="Name"
                value={row.name ?? ''}
                disabled={disabled}
                onChange={(event) => {
                  update(row.rowId, { name: event.target.value });
                }}
              />
              {preview ? (
                <Input
                  aria-label="App store URL"
                  value={row.appStoreUrl ?? ''}
                  disabled={disabled}
                  onChange={(event) => {
                    update(row.rowId, { appStoreUrl: event.target.value });
                  }}
                />
              ) : null}
              <Input
                aria-label="App store ID"
                value={row.appStoreId ?? ''}
                disabled={disabled}
                onChange={(event) => {
                  update(row.rowId, { appStoreId: event.target.value });
                }}
              />
              <Select
                value={row.platform?.id ?? ''}
                disabled={disabled}
                onValueChange={(id) => {
                  const platform = platforms.find((candidate) => candidate.id === id);
                  update(row.rowId, { platform: platform ?? { id, name: id } });
                }}
              >
                <SelectTrigger aria-label="Platform">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map((platform) => (
                    <SelectItem key={platform.id} value={platform.id}>
                      {platform.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                aria-label="Bundle (vanity) ID"
                value={row.bundleId ?? ''}
                disabled={disabled}
                onChange={(event) => {
                  update(row.rowId, { bundleId: event.target.value });
                }}
              />
              <Input
                aria-label="App domain"
                value={row.domain ?? ''}
                disabled={disabled}
                onChange={(event) => {
                  update(row.rowId, { domain: event.target.value });
                }}
              />
              <AutocompleteFilter
                label="IAB categories"
                options={iabCategories.map((category) => ({
                  id: category.id,
                  label: category.name,
                }))}
                selectedIds={row.iabCategoriesIds}
                disabled={disabled}
                selectAll={false}
                onSelectedIdsChange={(ids) => {
                  update(row.rowId, { iabCategoriesIds: ids });
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove app"
                disabled={disabled || (!preview && rows.length === 1)}
                onClick={() => {
                  onRowsChange(rows.filter((candidate) => candidate.rowId !== row.rowId));
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
      {!preview ? (
        <div className="flex flex-wrap items-center gap-3">
          <Label htmlFor="publisher-app-rows-to-add">Add rows</Label>
          <Input
            id="publisher-app-rows-to-add"
            className="w-24"
            type="number"
            min={1}
            max={100}
            value={rowsToAdd}
            disabled={disabled}
            onChange={(event) => {
              setRowsToAdd(event.target.value);
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={disabled || Number(rowsToAdd) < 1 || Number(rowsToAdd) > 100}
            onClick={() => {
              onRowsChange([
                ...rows,
                ...Array.from({ length: Number(rowsToAdd) }, createPublisherAppDraft),
              ]);
            }}
          >
            <Plus /> Add
          </Button>
          <span className="text-text-secondary ml-auto text-xs">* required columns</span>
        </div>
      ) : null}
    </div>
  );
}
